import { AllowedAdminUsernames } from "../types/auth.type";
import type { AuthTokenPayload } from "./types";

const PLATFORM_ADMIN_USERNAMES = new Set(
  Object.values(AllowedAdminUsernames).map((u) => String(u).toLowerCase()),
);

export function isPlatformAdmin(payload: AuthTokenPayload | null | undefined): boolean {
  if (!payload) {
    return false;
  }
  const username = payload.username?.toLowerCase();
  if (username && PLATFORM_ADMIN_USERNAMES.has(username)) {
    return true;
  }
  return (payload.platform_permissions ?? []).includes("*");
}

/** Flat permission strings from JWT (empty when legacy dict — use dual-mode for gates). */
export function permissionsFromPayload(payload: AuthTokenPayload | null | undefined): string[] {
  if (!payload) {
    return [];
  }
  const raw = payload.permissions;

  if (Array.isArray(raw) && raw.length > 0) {
    return raw.filter(Boolean).map(String);
  }

  return [];
}

export function isLegacyPermissionsDict(
  raw: AuthTokenPayload["permissions"] | undefined,
): raw is Record<string, string[]> {
  return !!raw && typeof raw === "object" && !Array.isArray(raw);
}

export function getLegacyRolesForOrg(
  payload: AuthTokenPayload | null | undefined,
  orgId: string,
): string[] {
  if (!payload || !orgId) {
    return [];
  }
  const raw = payload.permissions;
  if (!isLegacyPermissionsDict(raw)) {
    return [];
  }
  const wildcard = raw["*"] ?? [];
  const orgRoles = raw[orgId] ?? [];
  return [...new Set([...wildcard, ...orgRoles].map(String))];
}

export function getAccessibleOrgIds(payload: AuthTokenPayload | null | undefined): string[] {
  if (!payload) {
    return [];
  }
  const raw = payload.permissions;

  if (isLegacyPermissionsDict(raw)) {
    return Object.keys(raw).filter((k) => k !== "*");
  }

  const flat = permissionsFromPayload(payload);
  const orgIds = new Set<string>();

  for (const perm of flat) {
    if (!perm.startsWith("recyclee.") || perm.startsWith("recyclee.admin.")) {
      continue;
    }
    const parts = perm.split(".");
    if (parts.length >= 2 && parts[1]) {
      orgIds.add(parts[1]);
    }
  }

  return [...orgIds];
}

export function hasOrgAccess(
  permissionList: readonly string[],
  orgId: string,
  payload?: AuthTokenPayload | null,
): boolean {
  if (payload && isPlatformAdmin(payload)) {
    return true;
  }
  if (!orgId) {
    return false;
  }
  const prefix = `recyclee.${orgId}.`;
  return permissionList.some((p) => p.startsWith(prefix));
}

export function getSitesForOrg(
  payload: AuthTokenPayload | null | undefined,
  orgId: string,
): string[] | "all" | "none" {
  const sites = payload?.sites ?? {};
  const orgSites = sites[orgId];
  if (!orgSites || orgSites.length === 0) {
    return "none";
  }
  if (orgSites.includes("*")) {
    return "all";
  }
  return orgSites;
}

export function canAccessSite(
  payload: AuthTokenPayload | null | undefined,
  orgId: string,
  siteId: string,
): boolean {
  const sites = getSitesForOrg(payload, orgId);
  if (sites === "all") {
    return true;
  }
  if (sites === "none") {
    return false;
  }
  return sites.includes(siteId);
}

export function tenantPermission(
  orgId: string,
  siteId: string,
  entity: string,
  action: string,
): string {
  return `recyclee.${orgId}.${siteId}.${entity}.${action}`;
}

export function orgScopedPermission(orgId: string, entity: string, action: string): string {
  return `recyclee.${orgId}.*.${entity}.${action}`;
}

export function platformPermission(entity: string, action: string): string {
  return `recyclee.admin.${entity}.${action}`;
}

/** Build AuthTokenPayload from decoded JWT (handles permissions at root or under data). */
export function normalizeTokenPayload(decoded: Record<string, unknown>): AuthTokenPayload {
  const data = decoded.data as Record<string, unknown> | undefined;
  const permissions =
    (decoded.permissions as AuthTokenPayload["permissions"]) ??
    (data?.permissions as AuthTokenPayload["permissions"]) ??
    [];
  const platform_permissions =
    (decoded.platform_permissions as string[] | undefined) ??
    (data?.platform_permissions as string[] | undefined);
  const sites =
    (decoded.sites as Record<string, string[]> | undefined) ??
    (data?.sites as Record<string, string[]> | undefined);
  return {
    sub: String(decoded.sub ?? ""),
    username: String(decoded.username ?? ""),
    typ: String(decoded.typ ?? "access"),
    exp: Number(decoded.exp ?? 0),
    iat: Number(decoded.iat ?? 0),
    permissions,
    platform_permissions,
    sites,
    is_pilot: decoded.is_pilot === true || data?.is_pilot === true,
  };
}
